import { zodResolver } from "@hookform/resolvers/zod";
import { createRegionSchema, type Region } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MapPinned, Plus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import { api } from "../../../lib/api.js";
import { applyFieldErrors, errorMessage } from "../../../lib/errors.js";
import { useDocumentTitle } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";

export const Route = createFileRoute("/_app/ayarlar/bolgeler")({ component: RegionsPage });

type RegionValues = z.input<typeof createRegionSchema>;

function RegionDialog({ region, onClose }: { region: Region | null; onClose: () => void }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<RegionValues>({
    resolver: zodResolver(createRegionSchema),
    defaultValues: { name: region?.name ?? "" },
  });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      if (region) await api.PATCH("/regions/{id}", { params: { path: { id: region.id } }, body: values });
      else await api.POST("/regions", { body: values });
      await queryClient.invalidateQueries({ queryKey: queryKeys.regions });
      toast({ tone: "success", title: t("common.saved") });
      onClose();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, ["name"]).length > 0 || !form.formState.errors.name)
        setFormError(errorMessage(error));
    }
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={region ? t("regions.rename") : t("regions.add")}
    >
      <form onSubmit={(event) => void submit(event)} className="grid gap-4" noValidate>
        {formError ? <Alert tone="danger">{formError}</Alert> : null}
        <FormField label={t("regions.fields.name")} error={form.formState.errors.name?.message} required>
          {(control) => <Input {...control} {...form.register("name")} autoFocus />}
        </FormField>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RegionsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  useDocumentTitle(t("menu.regions"));
  const [dialog, setDialog] = useState<{ region: Region | null } | null>(null);
  const regions = useQuery({
    queryKey: queryKeys.regions,
    queryFn: async () => (await api.GET("/regions")).data!.items,
  });

  const setActive = useMutation({
    mutationFn: async (input: { id: string; isActive: boolean }) =>
      api.PATCH("/regions/{id}", { params: { path: { id: input.id } }, body: { isActive: input.isActive } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.regions }),
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const columns = useMemo<DataTableColumn<Region>[]>(
    () => [
      {
        id: "name",
        header: t("regions.fields.name"),
        hideable: false,
        cell: (row) => <span className="font-medium">{row.name}</span>,
      },
      {
        id: "stationCount",
        header: t("regions.fields.stationCount"),
        align: "right",
        cell: (row) => <span className="tabular">{row.stationCount}</span>,
      },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => (
          <StatusBadge tone={row.isActive ? "success" : "neutral"}>
            {row.isActive ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        ),
      },
      {
        id: "actions",
        header: t("common.actions"),
        align: "right",
        hideable: false,
        cell: (row) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setDialog({ region: row })}>
              {t("regions.rename")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={setActive.isPending}
              onClick={() => setActive.mutate({ id: row.id, isActive: !row.isActive })}
            >
              {row.isActive ? t("common.deactivate") : t("common.activate")}
            </Button>
          </div>
        ),
      },
    ],
    [t, setActive],
  );

  return (
    <>
      <PageHeader
        title={t("menu.regions")}
        description={t("regions.description")}
        breadcrumb={[{ label: t("menu.groups.settings") }, { label: t("menu.regions") }]}
        actions={
          <Button onClick={() => setDialog({ region: null })}>
            <Plus aria-hidden="true" />
            {t("regions.add")}
          </Button>
        }
      />
      <DataTable
        caption={t("menu.regions")}
        columns={columns}
        rows={regions.data ?? []}
        rowKey={(row) => row.id}
        loading={regions.isPending}
        error={regions.error ? <Alert tone="danger">{errorMessage(regions.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            icon={<MapPinned />}
            title={t("regions.empty.title")}
            description={t("regions.empty.description")}
          />
        }
      />
      {dialog ? (
        <RegionDialog
          key={dialog.region?.id ?? "new"}
          region={dialog.region}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
